import { Component, input, output, signal } from "@angular/core";

import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { finalize } from "rxjs";
import { ToastrService } from "ngx-toastr";
import { IVerifyResetCode_Request } from "../../../Utils/_services/account/account.model";
import { AccountService } from "../../../Utils/_services/account/account.service";
import { LeftUiTemplateComponent } from "../left-ui-template/left-ui-template.component";
import { AdminService } from "../../../Utils/_services/admin/admin/admin.service";

@Component({
  selector: "app-verify-code",
  standalone: true,
  imports: [FormsModule, LeftUiTemplateComponent],
  templateUrl: "./verify-code.component.html",
  styleUrl: "./verify-code.component.css",
})
export class VerifyCodeComponent {
  constructor(
    private router: Router,
    private toast: ToastrService,
    private accountService: AccountService,
    private adminService: AdminService,
    private route: ActivatedRoute
  ) { }
  mode = input<'forgot-password' | 'admin-verify'>('forgot-password');
  isVerifying = signal(false);
  isResendingCode = signal(false);;
  resetExpireTimeChange = output<number>();
  code = signal<string[]>(["", "", "", "", "", ""]);
  timeRemaining = signal<string>("04:57");
  email = signal<string>("");
  resetExpireTime = signal<number>(0);
  private timer: any;
currentMode : string = '';
rememberMe : boolean = false;
ngOnInit() {
  this.rememberMe = JSON.parse(sessionStorage.getItem('rememberMe') || 'false');

  const routeMode = this.route.snapshot.data?.['mode'];
  if (routeMode) {
    this.currentMode = routeMode;
  }

  this.route.queryParams.subscribe(params => {
    if (params['email'] && params['resetExpireTime']) {
      this.email.set(params['email']);
      this.resetExpireTime.set(params['resetExpireTime']);
      this.startTimer();
      return;
    }

    const state = history.state;
    if (state?.email && state?.resetExpireTime) {
      this.email.set(state.email);
      this.resetExpireTime.set(state.resetExpireTime);
      this.startTimer();
    } else {
      this.router.navigate(["/auth/forgot-password"]);
    }
  });
}

  ngOnDestroy() {
    sessionStorage.removeItem('rememberMe');
    if (this.timer) {
      clearInterval(this.timer);
    }
  }

  onInput(event: any, index: number) {
    const value = event.target.value;

    const numericValue = value.replace(/\D/g, "");
    event.target.value = numericValue;

    if (value.length > 1) {
      event.target.value = value.slice(-1);
    }

    const newCode = [...this.code()];
    newCode[index] = event.target.value;
    this.code.set(newCode);

    // Auto-focus next input
    if (event.target.value && index < 5) {
      const nextInput =
        event.target.parentElement.nextElementSibling?.querySelector("input");
      nextInput?.focus();
    }
  }

  onKeyDown(event: KeyboardEvent, index: number) {
    // Handle backspace to move to previous input
    if (event.key === "Backspace" && !this.code()[index] && index > 0) {
      const prevInput = (
        event.target as HTMLElement
      ).parentElement?.previousElementSibling?.querySelector("input");
      prevInput?.focus();
    }
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData("text");

    if (pastedData && /^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split("");
      this.code.set(digits);

      // Fill all inputs
      const inputs = document.querySelectorAll(".code-input input");
      digits.forEach((digit, i) => {
        if (inputs[i]) {
          (inputs[i] as HTMLInputElement).value = digit;
        }
      });

      // Focus last input
      (inputs[5] as HTMLInputElement)?.focus();
      if (this.code().length == 6) {
        this.verifyCode();
      }
    }
  }

  verifyCode() {
    this.isVerifying.set(true);
    const fullCode = this.code().join("");
    if (fullCode.length !== 6) {
      this.toast.error("Please enter complete code");
      this.isVerifying.set(false);
      return;
    }

    if (this.currentMode === 'admin-verify') {
      this.adminService.verifyAdminLogin({
        email: this.email(),
        verificationCode: fullCode
      }, this.rememberMe).pipe(
        finalize(() => this.isVerifying.set(false))
      ).subscribe({
        next: res => {
          if (res?.auth?.token) {
            this.toast.success("Logged in successfully");
            this.router.navigate(["/admin/dashboard"]);
          }
        },
        error: (res) => {
          this.toast.error(res.error.msg);
        }
      });
    } else {
      const req: IVerifyResetCode_Request = {
        email: this.email(),
        verificationCode: fullCode,
      };
      this.accountService.verifyResetCode(req)
        .pipe(finalize(() => this.isVerifying.set(false)))
        .subscribe({
          next: res => {
            if (res.data?.codeVerified) {
              this.router.navigate(["/auth/reset-password"], {
                state: { email: this.email(), code: fullCode }
              });
            } else {
              this.toast.error(res.msg);
            }
          },
          error: (res) => {
            this.toast.error(res.error.msg);
          }
        });
    }
  }

  resendCode() {
    this.isResendingCode.set(true);

    if (this.currentMode === 'admin-verify') {
      this.isResendingCode.set(true);
      this.adminService.resendAdminLoginOtp(this.email())
        .pipe(finalize(() => this.isResendingCode.set(false)))
        .subscribe({
          next: res => {
            if (res.data?.emailSent) {
              this.code.set(["", "", "", "", "", ""]);
              const inputs = document.querySelectorAll(".code-input input");
              inputs.forEach(input => (input as HTMLInputElement).value = "");
              (inputs[0] as HTMLInputElement)?.focus();
              this.resetExpireTime.set(res.data.resetExpireTime);
              this.startTimer();
              this.toast.success("A new verification code has been sent to your email.");
            }
          },
          error: (res) => {
            this.toast.error(res.error.msg);
          }
        });
      return;
    }
    this.accountService
      .forgot({ email: this.email() })
      .pipe(
        finalize(() => {
          this.isResendingCode.set(false);
        })
      )
      .subscribe({
        next: res => {
          if (res.data.emailSent) {
            this.code.set(["", "", "", "", "", ""]);
            const inputs = document.querySelectorAll(".code-input input");
            inputs.forEach(input => {
              (input as HTMLInputElement).value = "";
            });
            (inputs[0] as HTMLInputElement)?.focus();
            this.timeRemaining.set("04:57");
            this.resetExpireTimeChange.emit(res.data.resetExpireTime);
            this.startTimer();
          }
        },
        error: err => { },
      });
  }

  private startTimer() {
    clearInterval(this.timer);

    const tick = () => {
      const diff = this.resetExpireTime() - Date.now();
      if (diff <= 0) {
        this.timeRemaining.set("00:00");
        clearInterval(this.timer);
        return;
      }
      const totalSeconds = Math.floor(diff / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      this.timeRemaining.set(
        `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
      );
    };

    tick();
    this.timer = setInterval(tick, 1000);
  }
}
